import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiErrorDetail } from "@/lib/api";

export default function AdminSubscribers() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/subscribers");
      setList(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Remove this subscriber?")) return;
    try {
      await api.delete(`/admin/subscribers/${id}`);
      toast.success("Subscriber removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
    }
  };

  const downloadCsv = async () => {
    try {
      const token = localStorage.getItem("bruff_token");
      const res = await fetch(`${API}/admin/subscribers/export.csv`, {
        headers: { Authorization: token ? `Bearer ${token}` : "" },
        credentials: "include",
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "subscribers.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download CSV");
    }
  };

  return (
    <div className="bg-paper min-h-[80vh]" data-testid="admin-subscribers-page">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-16 pb-24">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink-soft hover:text-terracotta mb-8"
          data-testid="admin-subs-back-link"
        >
          <ArrowLeft size={14} /> Dashboard
        </Link>

        <div className="flex items-end justify-between border-b border-rule pb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-terracotta mb-3">
              Newsletter
            </p>
            <h1 className="font-serif-display text-3xl lg:text-4xl text-ink tracking-tight">
              Subscribers
              <span className="ml-3 text-2xl text-ink-soft" data-testid="admin-subs-count">
                ({list.length})
              </span>
            </h1>
          </div>
          <button
            onClick={downloadCsv}
            disabled={list.length === 0}
            data-testid="admin-subs-export-btn"
            className="inline-flex items-center gap-2 px-5 py-3 border border-ink text-ink text-xs tracking-[0.18em] uppercase hover:bg-ink hover:text-paper transition-colors disabled:opacity-50"
          >
            <Download size={16} /> Export CSV
          </button>
        </div>

        <div className="mt-10">
          {loading ? (
            <p className="text-ink-soft" data-testid="admin-subs-loading">Loading…</p>
          ) : list.length === 0 ? (
            <div className="py-16 text-center" data-testid="admin-subs-empty">
              <p className="font-serif-display text-2xl italic text-ink-soft">
                No subscribers yet — share your link.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-rule" data-testid="admin-subs-list">
              {list.map((s, i) => (
                <li
                  key={s.id}
                  data-testid={`admin-subs-row-${i}`}
                  className="py-4 grid grid-cols-12 gap-4 items-center"
                >
                  <span className="col-span-7 text-base text-ink break-all">
                    {s.email}
                  </span>
                  <span className="col-span-4 text-xs text-ink-soft tracking-wide">
                    {s.created_at
                      ? new Date(s.created_at).toLocaleString(undefined, {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                        })
                      : ""}
                  </span>
                  <button
                    onClick={() => remove(s.id)}
                    title="Remove subscriber"
                    data-testid={`admin-subs-delete-${i}`}
                    className="col-span-1 justify-self-end text-ink-soft hover:text-terracotta"
                  >
                    <Trash2 size={16} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
